import axios from 'axios'
import { ActionTree } from 'vuex'
import { Response } from '../response'
import { ApiVersion, RootState } from '../types'
import { DoctorMutations } from './mutations'
import {
  Doctor,
  DoctorOpinion,
  DoctorPayload,
  DoctorState,
  SingleParam,
  UpdateNameRequest
} from './types'
import { HTTPCode } from '@/store/types'

import UserStorage from '@/utils/Storage/UserStorage'
import { NotificationType, showNotification } from '@/utils/Notification'
const userStorage = UserStorage.getInstance()

export const actions: ActionTree<DoctorState, RootState> = {
  //Get user's doctor info
  async getDoctorInfo({ commit, dispatch }): Promise<Response<Doctor>> {
    commit(DoctorMutations.SET_LOADER, true)
    try {
      const { data } = await axios.get(`${ApiVersion.V1}/Doctors`)
      const response: Response<Doctor> = data
      console.log(data)
      if (response.succeeded) {
        userStorage.setDoctor({ id: response.data?.doctorId })
        axios.defaults.headers.doctorId = response.data?.doctorId ?? ''
        commit(DoctorMutations.SET_DOCTOR, response)
        if (response.message != 'No se encontró el doctor buscado') {
          dispatch('notifications/getNotifications', null, { root: true })
        }
        return response
      } else {
        return response
      }
    } catch (error) {
      console.error(error)
      return { succeeded: false, message: '', errors: [] }
    } finally {
      commit(DoctorMutations.SET_LOADER, false)
    }
  },

  //Get doctor info by id
  async getDoctorById({ commit }, id: number) {
    commit(DoctorMutations.SET_LOADER, true)
    try {
      const response: Response<Doctor> = await axios.get(
        `${ApiVersion.V1}/Doctors/${id}`
      )
      commit(DoctorMutations.SET_DOCTOR, response.data)
    } catch (error) {
      console.error(error)
    } finally {
      commit(DoctorMutations.SET_LOADER, false)
    }
  },

  //Update doctor name
  async updateDoctorName(
    { commit, dispatch },
    doctorPayload: DoctorPayload<UpdateNameRequest>
  ) {
    commit(DoctorMutations.SET_LOADER_INFO, true)
    try {
      await axios.patch(`${ApiVersion.V1}/Doctors/name`, doctorPayload.payload)
      dispatch('getDoctorInfo')
    } catch (error) {
      console.error(error)
    } finally {
      commit(DoctorMutations.SET_LOADER_INFO, false)
    }
  },

  //Update doctor description
  async updateDoctorDescription(
    { commit, dispatch },
    doctorPayload: DoctorPayload<SingleParam>
  ) {
    commit(DoctorMutations.SET_LOADER_INFO, true)
    try {
      await axios.patch(
        `${ApiVersion.V1}/Doctors/description`,
        doctorPayload.payload
      )
      dispatch('getDoctorInfo')
    } catch (error) {
      console.error(error)
    } finally {
      commit(DoctorMutations.SET_LOADER_INFO, false)
    }
  },

  //Update doctor treatment
  async updateDoctorTreatment(
    { commit, dispatch },
    doctorPayload: DoctorPayload<SingleParam>
  ) {
    commit(DoctorMutations.SET_LOADER_INFO, true)
    try {
      await axios.patch(
        `${ApiVersion.V1}/Doctors/treatment`,
        doctorPayload.payload
      )
      dispatch('getDoctorInfo')
    } catch (error) {
      console.error(error)
    } finally {
      commit(DoctorMutations.SET_LOADER_INFO, false)
    }
  },

  //Assign module to doctor consultory
  async assignModuleToDoctorConsultory(
    { commit },
    doctorPayload: DoctorPayload<number>
  ) {
    try {
      commit(DoctorMutations.SET_LOADER, false)
      await axios.post(
        `${ApiVersion.V1}/Doctors/${doctorPayload.entityId}/modules/${doctorPayload.payload}`
      )
    } catch (error) {
      console.error(error)
    } finally {
      commit(DoctorMutations.SET_LOADER, false)
    }
  },

  //End consultation
  async endConsultation({ commit }, doctorPayload: DoctorPayload<number>) {
    try {
      commit(DoctorMutations.SET_LOADER, false)
      await axios.post(
        `${ApiVersion.V1}/Doctors/${doctorPayload.entityId}/consultation/${doctorPayload.payload}/end`
      )
    } catch (error) {
      console.error(error)
    } finally {
      commit(DoctorMutations.SET_LOADER, false)
    }
  },

  //Refuse consultation
  async refuseConsultation({ commit }, doctorPayload: DoctorPayload<number>) {
    try {
      commit(DoctorMutations.SET_LOADER, false)
      axios.post(
        `${ApiVersion.V1}/Doctors/${doctorPayload.entityId}/consultation/${doctorPayload.payload}/refuse`
      )
    } catch (error) {
      console.error(error)
    } finally {
      commit(DoctorMutations.SET_LOADER, false)
    }
  },

  //Get doctor info by id
  async getDoctorOpinions({ commit }) {
    commit(DoctorMutations.SET_LOADER, true)
    try {
      const { data } = await axios.get(`${ApiVersion.V1}/Doctors/opinions`)
      const response: Response<Array<DoctorOpinion>> = data
      commit(DoctorMutations.SET_DOCTOR_OPINIONS, response.data)
    } catch (error: any) {
      console.error(error)
      const code: number = error?.response?.status
      if (code == HTTPCode.NotFound || code == HTTPCode.Unauthorized) {
        showNotification(
          NotificationType.SUCCESS,
          error?.response?.message,
          2000
        )
      }
    } finally {
      commit(DoctorMutations.SET_LOADER, false)
    }
  },

  //Update consultation price
  async updateConsultationPrice({ commit, dispatch }, payload: SingleParam) {
    commit(DoctorMutations.SET_LOADER_INFO, true)
    try {
      let resp = await axios.patch(
        `${ApiVersion.V1}/Doctors/consultation-price`,
        payload
      )
      if (resp?.data?.succeeded === true) {
        dispatch('getDoctorInfo')
        return true
      } else {
        return false
      }
    } catch (error) {
      console.error(error)
      return false
    } finally {
      commit(DoctorMutations.SET_LOADER_INFO, false)
    }
  },

  //Update obstetric price
  async updateObstetricPrice({ commit, dispatch }, payload: SingleParam) {
    commit(DoctorMutations.SET_LOADER_INFO, true)
    try {
      let resp = await axios.patch(
        `${ApiVersion.V1}/Doctors/obstetric-event-price`,
        payload
      )
      if (resp?.data?.succeeded === true) {
        dispatch('getDoctorInfo')
        return true
      } else {
        return false
      }
    } catch (error) {
      console.error(error)
      return false
    } finally {
      commit(DoctorMutations.SET_LOADER_INFO, false)
    }
  },

  //Update obstetric price
  async updateImageProfile({ commit, dispatch }, file: File) {
    commit(DoctorMutations.SET_LOADER_INFO, true)
    try {
      let InstFormData = new FormData()
      InstFormData.append('image', file)
      await axios.post(`${ApiVersion.V1}/Doctors/profileImage`, InstFormData, {
        headers: { 'content-type': 'multipart/form-data' }
      })
      dispatch('getDoctorInfo')
    } catch (error) {
      console.error(error)
    } finally {
      commit(DoctorMutations.SET_LOADER_INFO, false)
    }
  }
}
